import { catalog } from './catalog.js';
import { filters } from './filters.js';
import { settings } from './storage.js';
import { qualityOf, resolutionOf } from './utils.js';

/* Los filtros que la persona eligió en TV en directo, Películas y Series (proveedor, país, categorías, calidad) también valen para el
   Inicio: «Continuar viendo», los canales recientes, las novedades, las recomendaciones… no enseñan nada que esos filtros dejen fuera.
   Solo cuentan los filtros que siguen existiendo (si el país elegido ya no está en el catálogo, ese filtro no se aplica). Las listas
   que la persona armó a mano (Mi lista, Favoritos) no se filtran. Se apaga en Ajustes → Aspecto. */

export const matchesQuality = (name, id) => (/^R\d+$/.test(String(id)) ? resolutionOf(name) === Number(String(id).slice(1)) : qualityOf(name) === id);

const NONE = 'none';

/**
 * Predicado para una sección a partir de lo guardado y de lo que existe ahora; null si no hay ningún filtro que aplicar.
 * `ctx`: { providerIds: [id], countries: [id|''], cats: [id], inProvider(item, id) }.
 */
export function buildPredicate(saved, ctx, { live = false } = {}) {
    saved = saved || {};
    const providers = (Array.isArray(saved.providers) ? saved.providers.map(String) : []).filter((id) => ctx.providerIds.indexOf(id) !== -1);
    // El país solo es un filtro si el catálogo trae al menos dos (igual que el selector de país).
    const countryIds = ctx.countries.map((c) => c || NONE);
    const countriesShown = ctx.countries.length > 1 && ctx.countries.some((c) => c);
    const countries = countriesShown ? (Array.isArray(saved.countries) ? saved.countries.map(String) : []).filter((id) => countryIds.indexOf(id) !== -1) : [];
    const cats = (Array.isArray(saved.cats) ? saved.cats.map(String) : []).filter((id) => ctx.cats.indexOf(id) !== -1);
    const quality = live && saved.quality && saved.quality !== 'all' ? String(saved.quality) : '';
    if (!providers.length && !countries.length && !cats.length && !quality) return null;
    const cset = new Set(countries);
    const kset = new Set(cats);
    const pred = (item) => {
        if (!item) return false;
        if (providers.length && !providers.some((id) => ctx.inProvider(item, id))) return false;
        if (cset.size && !cset.has(item.region || NONE)) return false;
        if (kset.size && !kset.has(String(item.categoryId))) return false;
        if (quality && !matchesQuality(item.name, quality)) return false;
        return true;
    };
    pred.cats = cats;
    pred.signature = JSON.stringify([providers, countries, cats, quality]);
    return pred;
}

const NOOP = { on: false, signature: '', pass: () => true, list: (kind, items) => items, entries: (rows) => rows, cats: () => [] };

/** Los filtros de Inicio de ahora mismo (se calcula una vez por pintado). */
export function homeFilter() {
    if (settings.get('homeFilters') === false) return NOOP;
    const make = (kind) => buildPredicate(filters.get(kind), {
        providerIds: catalog.providerChoices().map((p) => p.id),
        countries: (catalog.countries[kind] || []).map((c) => c.id),
        cats: (catalog.categories[kind] || []).map((c) => String(c.id)),
        inProvider: (item, id) => catalog.inProvider(item, id)
    }, { live: kind === 'live' });
    const by = { live: make('live'), vod: make('vod'), series: make('series') };
    if (!by.live && !by.vod && !by.series) return NOOP;
    const sectionOf = (kind) => (kind === 'episode' ? 'series' : kind);
    const pass = (kind, item) => { const p = by[sectionOf(kind)]; return !p || p(item); };
    return {
        on: true,
        signature: [by.live, by.vod, by.series].map((p) => (p ? p.signature : '')).join('|'),
        pass,
        /** Items de un tipo ('live' | 'vod' | 'series'). */
        list: (kind, items) => (by[sectionOf(kind)] ? items.filter((it) => by[sectionOf(kind)](it)) : items),
        /** Filas mixtas [{ kind, item }] (continuar viendo, novedades, recomendaciones…). */
        entries: (rows) => rows.filter((r) => pass(r.kind, r.item)),
        /** Categorías elegidas de ese tipo (para que el Inicio enseñe esas y no las primeras). */
        cats: (kind) => (by[sectionOf(kind)] ? by[sectionOf(kind)].cats : [])
    };
}
